/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: yongqiang
 * @LastEditTime: 2025-10-09 21:33:49
 */

import { useCallback, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { selectVerifyAuthConfig } from '../../slices/verifyAuthSlice'
import resultTruthTitle from '../../images/verify-auth/batch_result_truth_title.png'
import resultUnKnowTitle from '../../images/verify-auth/batch_result_unknow_title.png'
import resultFakeTitle from '../../images/verify-auth/batch_result_fake_title.png'
import errorIcon from '../../images/verify-auth/result_status_error_icon.png'
import noticeIcon from '../../images/verify-auth/result_status_notice_icon.png'
import rightIcon from '../../images/verify-auth/result_status_right_icon.png'
import warnIcon from '../../images/verify-auth/result_status_warning_icon.png'
import resultFakeStamp from '../../images/verify-auth/result_rect_stamp_fake.png'
import resultUnknowStamp from '../../images/verify-auth/result_rect_stamp_unKnow.png'
import resultTruthStamp from '../../images/verify-auth/result_rect_stamp_truth.png'

const useVerifyAuthMethods = () => {
  const config = useAppSelector(selectVerifyAuthConfig)

  /**
   * 为假的结果码
   */
  const fakeResultCodes = useMemo(() => {
    return config.resultCodes.fake.map((item) => item.value)
  }, [config])

  /**
   * 未知的结果码
   */
  const unknownResultCodes = useMemo(() => {
    return config.resultCodes.unknown.map((item) => item.value)
  }, [config])

  /**
   * 真的结果码
   */
  const truthResultCodes = useMemo(() => {
    return config.resultCodes.truth.map((item) => item.value)
  }, [config])

  const exceptionResultCodes = useMemo(() => {
    return config.resultCodes.serverException.map((item) => item.value)
  }, [config])

  /**
   * 是否为假
   * @param status 结果码
   * @returns 是否为假
   */
  const isFakeCheck = useCallback(
    (status: number) => {
      return fakeResultCodes.includes(status)
    },
    [fakeResultCodes]
  )

  /**
   * 获取假场景
   * @param status 结果码
   * @returns 假场景
   */
  const getFakeScene = useCallback(
    (status: number) => {
      return config.fakeConfigs.scenes.find((item) =>
        item.codes.includes(String(status))
      )
    },
    [config]
  )

  /**
   * 是否为未知
   * @param status 结果码
   * @returns 是否为未知
   */
  const isUnknownCheck = useCallback(
    (status: number) => {
      return (
        unknownResultCodes.includes(status) ||
        exceptionResultCodes.includes(status)
      )
    },
    [unknownResultCodes, exceptionResultCodes]
  )

  /**
   * 是否为异常
   * @param status 结果码
   * @returns 是否为异常
   */
  const isExceptionCheck = useCallback(
    (status: number) => {
      return exceptionResultCodes.includes(status)
    },
    [exceptionResultCodes]
  )

  /**
   * 获取未知场景
   * @param status 结果码
   * @returns 未知场景
   */
  const getUnKnowScene = useCallback(
    (status: number) => {
      return config.unknownConfigs.scenes.find((item) =>
        item.codes.includes(String(status))
      )
    },
    [config]
  )

  /**
   * 是否为真
   * @param status 结果码
   * @returns 是否为真
   */
  const isTruthCheck = useCallback(
    (status: number) => {
      return truthResultCodes.includes(status)
    },
    [config]
  )

  /**
   * 获取真场景
   * @param status 结果码
   * @returns 真场景
   */
  const getTruthScene = useCallback(
    (status: number) => {
      return config.truthConfigs.scenes.find((item) =>
        item.codes.includes(String(status))
      )
    },
    [config]
  )

  /**
   * 获取结果状态图片
   * @param status 结果码
   * @returns 结果状态图片
   */
  const getResultStatusImage = useCallback(
    (status: number) => {
      let obj = {
        width: 136,
        height: 58,
        url: isTruthCheck(status)
          ? resultTruthTitle
          : isUnknownCheck(status)
          ? resultUnKnowTitle
          : resultFakeTitle
      }
      if (isTruthCheck(status) && config.truthConfigs.resultImage) {
        obj = config.truthConfigs.resultImage
      } else if (isUnknownCheck(status) && config.unknownConfigs.resultImage) {
        obj = config.unknownConfigs.resultImage
      } else if (status !== 0 && config.fakeConfigs.resultImage) {
        obj = config.fakeConfigs.resultImage
      }
      return obj
    },
    [config]
  )

  const getResultStampImage = useCallback(
    (status: number) => {
      let obj: {
        width?: number
        height?: number
        y: number | string
        x: number | string
        url: string
      } = {
        width: 136,
        height: 58,
        y: 10,
        x: '46%',
        url: isTruthCheck(status)
          ? resultTruthStamp
          : isUnknownCheck(status)
          ? resultUnknowStamp
          : resultFakeStamp
      }
      if (isTruthCheck(status) && config.truthConfigs.stampImage) {
        obj = config.truthConfigs.stampImage
      } else if (isUnknownCheck(status) && config.unknownConfigs.stampImage) {
        obj = config.unknownConfigs.stampImage
      } else if (status !== 0 && config.fakeConfigs.stampImage) {
        obj = config.fakeConfigs.stampImage
      }
      return obj
    },
    [config]
  )

  const isPhotoDetailFeatureContent = useCallback((feature) => {
    return feature.content.includes('{{') && feature.content.includes('}}')
  }, [])

  /**
   * 获取特征值内容
   * @param feature 特征
   * @param photoDetail 照片详情
   * @returns 特征值内容
   */
  const getFeatureValueContent = useCallback(
    (feature, photoDetail) => {
      const keys = feature.content
        .replace('{{', '')
        .replace('}}', '')
        .split('.')
      let value = photoDetail
      keys.forEach((key: string) => {
        value = value[key] || ''
      })
      return value
    },
    [isPhotoDetailFeatureContent]
  )

  /**
   * 是否使用特征值兜底内容
   * @param feature 特征
   * @param photoDetail 照片详情
   * @returns 是否使用特征值兜底内容
   */
  const isUseFeatureFallback = useCallback(
    (feature, photoDetail) => {
      return getFeatureValueContent(feature, photoDetail) === ''
    },
    [getFeatureValueContent]
  )

  const getFeatureIcon = useCallback((feature) => {
    if (feature.customIcon) {
      return feature.customIcon
    }
    switch (feature.icon) {
      case 'error':
        return errorIcon
      case 'notice':
        return noticeIcon
      case 'right':
        return rightIcon
      case 'warn':
        return warnIcon
    }
    return ''
  }, [])

  const getFeature = useCallback(
    (feature, photoDetail) => {
      if (isPhotoDetailFeatureContent(feature)) {
        const { fallback } = feature
        const isUseFallback =
          isUseFeatureFallback(feature, photoDetail) && fallback
        return {
          icon: isUseFallback
            ? getFeatureIcon(fallback)
            : getFeatureIcon(feature),
          color: isUseFallback ? fallback.color : feature.color,
          title: isUseFallback ? fallback.title : feature.title,
          content: isUseFallback
            ? fallback.content
            : getFeatureValueContent(feature, photoDetail) ||
              feature.defaultContent
        }
      } else {
        return {
          icon: getFeatureIcon(feature),
          color: feature.color,
          title: feature.title,
          content: feature.content || feature.defaultContent
        }
      }
    },
    [getFeatureIcon, isUseFeatureFallback, getFeatureValueContent]
  )

  return {
    fakeResultCodes,
    exceptionResultCodes,
    isFakeCheck,
    unknownResultCodes,
    isUnknownCheck,
    getUnKnowScene,
    getFakeScene,
    isTruthCheck,
    getTruthScene,
    isExceptionCheck,
    getResultStatusImage,
    getResultStampImage,
    getFeature
  }
}

export default useVerifyAuthMethods
